import { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { SITE, SITE_STUDIO_TYPE, IS_STUDIO } from "@/config";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { updateSiteTextsCache } from "@/hooks/useSiteTexts";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, formatEuro, formatHour, STATUS_LABELS, STUDIO_LABELS, MEDIA_CATEGORY_LABELS } from "@/lib/api";
import {
  Lock,
  Loader2,
  LogOut,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Banknote,
  Euro,
  Hourglass,
  Wrench,
  Trash2,
  UserX,
  Mail,
  Settings2,
  AlertTriangle,
  ShieldCheck,
  ImagePlus,
  Link2,
} from "lucide-react";

const STATUS_BADGES = {
  en_attente_validation: "border-orange-500/40 bg-orange-500/10 text-orange-400",
  en_attente_paiement: "border-primary/40 bg-[hsl(var(--studio-yellow)/0.08)] text-primary",
  confirmee: "border-green-600/40 bg-green-600/10 text-green-400",
  reservee: "border-border bg-secondary text-foreground",
  annulee: "border-destructive/40 bg-destructive/10 text-destructive",
};

const EMAIL_STATUS_BADGES = {
  envoye: "border-green-600/40 bg-green-600/10 text-green-400",
  non_configure: "border-orange-500/40 bg-orange-500/10 text-orange-400",
  erreur: "border-destructive/40 bg-destructive/10 text-destructive",
};

const EMAIL_STATUS_LABELS = {
  envoye: "Envoyé",
  non_configure: "SMTP non configuré",
  erreur: "Erreur",
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const SITE_TEXT_SECTIONS = [
  {
    title: "Accueil — En-tête",
    fields: [
      { key: "hero_badge", label: "Badge (au-dessus du titre)" },
      { key: "hero_title_a", label: "Titre — ligne 1" },
      { key: "hero_title_accent_a", label: "Titre — suite ligne 1 (dorée)" },
      { key: "hero_title_b", label: "Titre — ligne 2" },
      { key: "hero_title_accent_b", label: "Titre — ligne 3 (dorée)" },
      { key: "hero_text", label: "Texte de présentation", multiline: true },
    ],
  },
  {
    title: "Accueil — Planning",
    fields: [
      { key: "espace_title", label: "Titre" },
      { key: "espace_subtitle", label: "Sous-titre", multiline: true },
    ],
  },
  {
    title: "Accueil — Tarifs & services",
    fields: [
      { key: "tarifs_title", label: "Titre" },
      { key: "tarifs_subtitle", label: "Sous-titre", multiline: true },
    ],
  },
  {
    title: "Accueil — Suppléments tournage",
    fields: [
      { key: "supplements_badge", label: "Badge" },
      { key: "supplements_title", label: "Titre" },
      { key: "supplements_subtitle", label: "Sous-titre", multiline: true },
    ],
  },
  {
    title: "Accueil — Casting",
    fields: [
      { key: "casting_badge", label: "Badge" },
      { key: "casting_title", label: "Titre" },
      { key: "casting_subtitle", label: "Sous-titre", multiline: true },
    ],
  },
  {
    title: "Accueil — Mariages",
    fields: [
      { key: "mariage_badge", label: "Badge" },
      { key: "mariage_title", label: "Titre" },
      { key: "mariage_subtitle", label: "Sous-titre", multiline: true },
    ],
  },
  {
    title: "Accueil — Galerie",
    fields: [
      { key: "galerie_badge", label: "Badge" },
      { key: "galerie_title", label: "Titre" },
      { key: "galerie_subtitle", label: "Sous-titre", multiline: true },
    ],
  },
  {
    title: "Accueil — Comment ça marche & appel à l'action",
    fields: [
      { key: "steps_title", label: "Titre de la section" },
      { key: "cta_title", label: "Titre de l'encart final" },
      { key: "cta_subtitle", label: "Texte de l'encart final", multiline: true },
    ],
  },
  {
    title: "Page Réserver",
    fields: [{ key: "reserver_title", label: "Titre (suivi du nom du site)" }],
  },
  {
    title: "Page Devis mariage",
    fields: [
      { key: "devis_badge", label: "Badge" },
      { key: "devis_title_a", label: "Titre — début" },
      { key: "devis_title_accent", label: "Titre — fin (dorée)" },
      { key: "devis_subtitle", label: "Sous-titre", multiline: true },
    ],
  },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token"));
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [blockedClients, setBlockedClients] = useState([]);
  const [settings, setSettings] = useState(null);
  const [siteTexts, setSiteTexts] = useState({});
  const [textEdits, setTextEdits] = useState({});
  const [emailLogs, setEmailLogs] = useState([]);
  const [weddingQuotes, setWeddingQuotes] = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [mediaForm, setMediaForm] = useState({ mode: "file", category: "teaser", title: "", link: "" });
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaProgress, setMediaProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ studio_type: "all", status: "all", date: "" });
  const [priceEdits, setPriceEdits] = useState({});
  const [settingsEdits, setSettingsEdits] = useState({});
  const [slotForm, setSlotForm] = useState({ studio_type: SITE_STUDIO_TYPE, date: "", start_hour: "9", end_hour: "12", reason: "" });
  const [clientForm, setClientForm] = useState({ email: "", reason: "" });

  const authHeaders = useCallback(() => ({ headers: { "X-Admin-Token": token } }), [token]);

  const logout = useCallback(() => {
    localStorage.removeItem("admin_token");
    setToken(null);
  }, []);

  const handleAuthError = useCallback(
    (e) => {
      if (e.response?.status === 401) {
        toast.error("Session expirée, reconnectez-vous");
        logout();
        return true;
      }
      return false;
    },
    [logout]
  );

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = {};
      if (filters.studio_type !== "all") params.studio_type = filters.studio_type;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.date) params.date = filters.date;
      const [statsRes, bookingsRes, servicesRes, slotsRes, clientsRes, settingsRes, logsRes, weddingRes, mediaRes, textsRes] = await Promise.all([
        api.get("/admin/stats", authHeaders()),
        api.get("/admin/bookings", { ...authHeaders(), params }),
        api.get("/admin/services", authHeaders()),
        api.get("/admin/blocked-slots", authHeaders()),
        api.get("/admin/blocked-clients", authHeaders()),
        api.get("/admin/settings", authHeaders()),
        api.get("/admin/email-logs", authHeaders()),
        api.get("/admin/mariage/devis", authHeaders()),
        api.get("/admin/media", authHeaders()),
        api.get("/site-texts"),
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setServices(servicesRes.data);
      setBlockedSlots(slotsRes.data);
      setBlockedClients(clientsRes.data);
      setSettings(settingsRes.data);
      setEmailLogs(logsRes.data);
      setWeddingQuotes(weddingRes.data);
      setMediaItems(mediaRes.data);
      setSiteTexts(textsRes.data);
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [token, filters, authHeaders, handleAuthError]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await api.post("/admin/login", { password });
      localStorage.setItem("admin_token", res.data.token);
      setToken(res.data.token);
      toast.success("Connexion réussie");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mot de passe incorrect");
    } finally {
      setLoggingIn(false);
    }
  };

  const WEDDING_STATUS_LABELS = {
    nouveau: "Nouveau",
    contacte: "Contacté",
    devis_envoye: "Devis envoyé",
    conclu: "Conclu",
    refuse: "Refusé",
  };

  const PHOTO_EXTS = ["jpg", "jpeg", "png", "webp", "gif"];
  const VIDEO_EXTS = ["mp4", "mov", "webm", "m4v"];

  const submitMedia = async (e) => {
    e.preventDefault();
    if (mediaForm.mode === "link") {
      if (!mediaForm.link.trim().startsWith("http")) {
        toast.error("Collez un lien YouTube ou Vimeo valide (https://…)");
        return;
      }
      setMediaUploading(true);
      try {
        await api.post(
          "/admin/media/link",
          { url: mediaForm.link.trim(), category: mediaForm.category, title: mediaForm.title },
          authHeaders()
        );
        toast.success("Vidéo ajoutée depuis le lien");
        setMediaForm({ ...mediaForm, title: "", link: "" });
        fetchAll();
      } catch (err) {
        if (!handleAuthError(err)) toast.error(err.response?.data?.detail || "Erreur lors de l'ajout du lien");
      } finally {
        setMediaUploading(false);
      }
      return;
    }
    if (!mediaFile) {
      toast.error("Choisissez un fichier photo ou vidéo");
      return;
    }
    const ext = mediaFile.name.split(".").pop().toLowerCase();
    const mediaType = PHOTO_EXTS.includes(ext) ? "photo" : VIDEO_EXTS.includes(ext) ? "video" : null;
    if (!mediaType) {
      toast.error(`Format .${ext} non supporté (photos : jpg/png/webp — vidéos : mp4/mov/webm)`);
      return;
    }
    setMediaUploading(true);
    setMediaProgress(0);
    try {
      const chunkSize = 4 * 1024 * 1024;
      const totalChunks = Math.max(1, Math.ceil(mediaFile.size / chunkSize));
      const uploadId =
        window.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}`;
      for (let i = 0; i < totalChunks; i++) {
        const chunk = mediaFile.slice(i * chunkSize, (i + 1) * chunkSize);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", String(i));
        fd.append("total_chunks", String(totalChunks));
        fd.append("file", chunk, mediaFile.name);
        await api.post("/admin/media/upload-chunk", fd, authHeaders());
        setMediaProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
      await api.post(
        "/admin/media/finalize",
        {
          upload_id: uploadId,
          filename: mediaFile.name,
          media_type: mediaType,
          category: mediaForm.category,
          title: mediaForm.title,
        },
        authHeaders()
      );
      toast.success(mediaType === "photo" ? "Photo ajoutée !" : "Vidéo ajoutée !");
      setMediaForm({ ...mediaForm, title: "" });
      setMediaFile(null);
      fetchAll();
    } catch (err) {
      if (!handleAuthError(err)) toast.error(err.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setMediaUploading(false);
      setMediaProgress(0);
    }
  };

  const toggleMediaActive = async (item) => {
    try {
      await api.put(`/admin/media/${item.id}`, { active: !item.active }, authHeaders());
      toast.success(!item.active ? "Média visible sur le site" : "Média masqué");
      fetchAll();
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Erreur lors de la mise à jour");
    }
  };

  const deleteMedia = async (item) => {
    try {
      await api.delete(`/admin/media/${item.id}`, authHeaders());
      toast.success("Média supprimé");
      fetchAll();
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Erreur lors de la suppression");
    }
  };

  const mediaSrc = (url) => (url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

  const updateWeddingQuoteStatus = async (quoteId, status) => {
    try {
      await api.put(`/admin/mariage/devis/${quoteId}/status`, { status }, authHeaders());
      toast.success("Statut de la demande mis à jour");
      fetchAll();
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const { data } = await api.put(`/admin/bookings/${bookingId}/status`, { status }, authHeaders());
      const messages = {
        reservee: "Réservation validée — email de confirmation envoyé au client",
        confirmee: "Réservation marquée comme payée",
        annulee:
          data?.refunded
            ? "Réservation annulée — remboursement Stripe automatique effectué, email envoyé au client"
            : "Réservation annulée — email envoyé au client",
      };
      toast.success(messages[status] || "Réservation mise à jour");
      fetchAll();
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erreur lors de la mise à jour");
    }
  };

  const blockClientFromBooking = async (booking) => {
    try {
      await api.post(
        "/admin/blocked-clients",
        {
          email: booking.customer?.email || "",
          phone: booking.customer?.phone || "",
          name: booking.customer?.name || "",
          reason: `Bloqué depuis la réservation ${booking.id.slice(0, 8)}`,
        },
        authHeaders()
      );
      toast.success(`Client ${booking.customer?.email} bloqué`);
      fetchAll();
    } catch (e) {
      if (!handleAuthError(e)) toast.error(e.response?.data?.detail || "Erreur lors du blocage");
    }
  };

  const savePrices = async (svc) => {
    const edits = priceEdits[svc.id] || {};
    const body = {};
    if (edits.price_onsite !== undefined && edits.price_onsite !== "") {
      body.price_onsite = parseFloat(edits.price_onsite);
    }
    if (edits.tiers && Object.keys(edits.tiers).length > 0) {
      const merged = { ...(svc.price_tiers || {}) };
      let changed = false;
      for (const [h, v] of Object.entries(edits.tiers)) {
        if (v !== "" && v !== undefined) {
          merged[h] = parseFloat(v);
          changed = true;
        }
      }
      if (body.price_onsite !== undefined && edits.tiers["1"] === undefined) {
        merged["1"] = body.price_onsite;
        changed = true;
      }
      if (changed) body.price_tiers = merged;
    }
    if (edits.name !== undefined && edits.name.trim() && edits.name.trim() !== svc.name) {
      body.name = edits.name.trim();
    }
    if (edits.description !== undefined && edits.description !== (svc.description || "")) {
      body.description = edits.description;
    }
    if (edits.features !== undefined) {
      const feats = edits.features.split("\n").map((s) => s.trim()).filter(Boolean);
      if (feats.join("\n") !== (svc.features || []).join("\n")) body.features = feats;
    }
    if (edits.promo_percent !== undefined && edits.promo_percent !== "") {
      body.promo_percent = parseFloat(edits.promo_percent);
    }
    if (edits.promo_until !== undefined && edits.promo_until !== (svc.promo_until || "")) {
      body.promo_until = edits.promo_until;
    }
    if (Object.keys(body).length === 0) {
      toast.info("Aucune modification");
      return;
    }
    try {
      await api.put(`/admin/services/${svc.id}`, body, authHeaders());
      toast.success(`Prestation mise à jour : ${svc.name}`);
      setPriceEdits((prev) => ({ ...prev, [svc.id]: {} }));
      fetchAll();
    } catch (e) {
      if (!handleAuthError(e)) toast.error(e.response?.data?.detail || "Erreur lors de la mise à jour du tarif");
    }
  };

  const toggleFromPrice = async (svc, checked) => {
    try {
      await api.put(`/admin/services/${svc.id}`, { from_price: checked }, authHeaders());
      toast.success(
        checked ? `« À partir de » activé : ${svc.name}` : `Prix fixe affiché : ${svc.name}`
      );
      fetchAll();
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erreur lors de la mise à jour");
    }
  };

  const togglePromo = async (svc, checked) => {
    try {
      await api.put(`/admin/services/${svc.id}`, { promo_active: checked }, authHeaders());
      if (checked && !svc.promo_percent) {
        toast.info(`Renseignez la remise (%) puis cliquez Enregistrer : ${svc.name}`);
      } else {
        toast.success(checked ? `Promotion activée : ${svc.name}` : `Promotion désactivée : ${svc.name}`);
      }
      fetchAll();
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erreur lors de la mise à jour");
    }
  };

  const saveSiteTexts = async () => {
    const body = {};
    for (const [k, v] of Object.entries(textEdits)) {
      if (v !== (siteTexts[k] ?? "")) body[k] = v;
    }
    if (Object.keys(body).length === 0) {
      toast.info("Aucune modification");
      return;
    }
    try {
      const res = await api.put("/admin/site-texts", body, authHeaders());
      setSiteTexts(res.data);
      updateSiteTextsCache(res.data);
      setTextEdits({});
      toast.success("Textes du site mis à jour");
    } catch (e) {
      if (!handleAuthError(e)) toast.error(e.response?.data?.detail || "Erreur lors de la mise à jour des textes");
    }
  };

  const createBlockedSlot = async (e) => {
    e.preventDefault();
    if (!slotForm.date) {
      toast.error("Choisissez une date");
      return;
    }
    try {
      await api.post(
        "/admin/blocked-slots",
        {
          studio_type: slotForm.studio_type,
          date: slotForm.date,
          start_hour: parseInt(slotForm.start_hour, 10),
          end_hour: parseInt(slotForm.end_hour, 10),
          reason: slotForm.reason || "Maintenance",
        },
        authHeaders()
      );
      toast.success("Créneaux bloqués");
      setSlotForm({ ...slotForm, date: "", reason: "" });
      fetchAll();
    } catch (err) {
      if (!handleAuthError(err)) toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const deleteBlockedSlot = async (id) => {
    try {
      await api.delete(`/admin/blocked-slots/${id}`, authHeaders());
      toast.success("Indisponibilité supprimée — créneaux réouverts");
      fetchAll();
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erreur lors de la suppression");
    }
  };

  const blockClientManual = async (e) => {
    e.preventDefault();
    try {
      await api.post(
        "/admin/blocked-clients",
        { email: clientForm.email, reason: clientForm.reason },
        authHeaders()
      );
      toast.success(`Client ${clientForm.email} bloqué`);
      setClientForm({ email: "", reason: "" });
      fetchAll();
    } catch (err) {
      if (!handleAuthError(err)) toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const unblockClient = async (id) => {
    try {
      await api.delete(`/admin/blocked-clients/${id}`, authHeaders());
      toast.success("Client débloqué");
      fetchAll();
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erreur");
    }
  };

  const saveSettings = async () => {
    const body = {};
    if (settingsEdits.manual_confirmation !== undefined) body.manual_confirmation = settingsEdits.manual_confirmation;
    if (settingsEdits.online_discount_percent !== undefined && settingsEdits.online_discount_percent !== "")
      body.online_discount_percent = parseFloat(settingsEdits.online_discount_percent);
    if (settingsEdits.company_email_enregistrement !== undefined && settingsEdits.company_email_enregistrement !== "")
      body.company_email_enregistrement = settingsEdits.company_email_enregistrement;
    if (settingsEdits.company_email_media !== undefined && settingsEdits.company_email_media !== "")
      body.company_email_media = settingsEdits.company_email_media;
    for (const key of ["social_instagram", "social_tiktok", "social_snapchat", "contact_phone"]) {
      if (settingsEdits[key] !== undefined && settingsEdits[key] !== "") body[key] = settingsEdits[key].trim();
    }
    if (Object.keys(body).length === 0) {
      toast.info("Aucune modification");
      return;
    }
    try {
      const res = await api.put("/admin/settings", body, authHeaders());
      setSettings(res.data);
      setSettingsEdits({});
      toast.success("Paramètres enregistrés");
      fetchAll();
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erreur lors de l'enregistrement");
    }
  };

  // ---------- Login ----------
  if (!token) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col px-4 py-20 sm:px-6">
        <Card className="border-border bg-card" data-testid="admin-login-card">
          <CardContent className="p-6 sm:p-8">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary">
              <Lock className="h-5 w-5 text-primary-foreground" />
            </span>
            <h1 className="mt-4 font-heading text-2xl font-semibold">Accès admin</h1>
            <p className="mt-1 text-sm text-muted-foreground">Espace réservé à {SITE.fullName}.</p>
            <form onSubmit={handleLogin} className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-password">Mot de passe</Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="admin-password-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loggingIn || !password} data-testid="admin-login-submit">
                {loggingIn ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                Se connecter
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Tableau de bord</h1>
          <p className="mt-1 text-sm text-muted-foreground">{SITE.fullName} — gestion des réservations</p>
        </div>
        <Button variant="outline" onClick={logout} data-testid="admin-logout-button">
          <LogOut className="mr-2 h-4 w-4" />
          Déconnexion
        </Button>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="admin-stats">
        {[
          { label: "Réservations", value: stats?.total ?? "—", icon: CalendarDays },
          { label: "À valider", value: stats?.a_valider ?? "—", icon: Hourglass, highlight: (stats?.a_valider || 0) > 0 },
          { label: "Confirmées / Payées", value: stats?.confirmees ?? "—", icon: CheckCircle2 },
          { label: "Paiement sur place", value: stats?.reservees ?? "—", icon: Banknote },
          { label: "Chiffre d'affaires", value: stats ? formatEuro(stats.revenue) : "—", icon: Euro },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} className={`bg-card ${stat.highlight ? "border-orange-500/50" : "border-border"}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {stat.label}
                  </span>
                  <Icon className={`h-4 w-4 ${stat.highlight ? "text-orange-400" : "text-primary"}`} />
                </div>
                <p className="mt-2 font-mono-nums text-2xl font-semibold">{stat.value}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="reservations" className="mt-10">
        <TabsList className="h-auto flex-wrap bg-secondary">
          <TabsTrigger value="reservations" data-testid="admin-tab-reservations">
            Réservations
            {bookings.filter((b) => b.status === "en_attente_validation").length > 0 && (
              <span
                className="ml-1.5 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white"
                data-testid="admin-badge-pending-bookings"
              >
                {bookings.filter((b) => b.status === "en_attente_validation").length}
              </span>
            )}
          </TabsTrigger>
          {!IS_STUDIO && (
            <TabsTrigger value="mariages" data-testid="admin-tab-mariages">
              Devis mariage
              {weddingQuotes.filter((q) => (q.status || "nouveau") === "nouveau").length > 0 && (
                <span
                  className="ml-1.5 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white"
                  data-testid="admin-badge-new-wedding-quotes"
                >
                  {weddingQuotes.filter((q) => (q.status || "nouveau") === "nouveau").length}
                </span>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="tarifs" data-testid="admin-tab-tarifs">Tarifs</TabsTrigger>
          <TabsTrigger value="textes" data-testid="admin-tab-textes">Textes</TabsTrigger>
          {!IS_STUDIO && (
            <TabsTrigger value="medias" data-testid="admin-tab-medias">Médias</TabsTrigger>
          )}
          <TabsTrigger value="indisponibilites" data-testid="admin-tab-indisponibilites">Indisponibilités</TabsTrigger>
          <TabsTrigger value="clients" data-testid="admin-tab-clients">Clients bloqués</TabsTrigger>
          <TabsTrigger value="parametres" data-testid="admin-tab-parametres">Paramètres</TabsTrigger>
        </TabsList>

        {/* ---------- Reservations ---------- */}
        <TabsContent value="reservations" className="mt-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Statut</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger className="w-[220px]" data-testid="admin-filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="en_attente_validation">En attente de validation</SelectItem>
                  <SelectItem value="confirmee">Confirmée / Payée</SelectItem>
                  <SelectItem value="reservee">Confirmée (sur place)</SelectItem>
                  <SelectItem value="en_attente_paiement">En attente de paiement</SelectItem>
                  <SelectItem value="annulee">Annulée</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Date</Label>
              <Input
                type="date"
                value={filters.date}
                onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                className="w-[170px]"
                data-testid="admin-filter-date"
              />
            </div>
            {filters.date && (
              <Button variant="ghost" size="sm" onClick={() => setFilters({ ...filters, date: "" })}>
                Effacer la date
              </Button>
            )}
          </div>

          <div className="mt-5 overflow-x-auto rounded-xl border border-border">
            <Table data-testid="admin-reservations-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Date</TableHead>
                  <TableHead>Horaire</TableHead>
                  <TableHead>Espace</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && bookings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                      Aucune réservation trouvée
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  bookings.map((b) => (
                    <TableRow key={b.id} className="border-border" data-testid={`admin-booking-row-${b.id}`}>
                      <TableCell className="whitespace-nowrap capitalize">
                        {format(parseISO(b.date), "EEE d MMM", { locale: fr })}
                      </TableCell>
                      <TableCell className="whitespace-nowrap font-mono-nums">
                        {formatHour(b.start_hour)}–{formatHour(b.end_hour)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">{STUDIO_LABELS[b.studio_type]}</TableCell>
                      <TableCell className="max-w-[170px] text-xs">
                        <p className="truncate">{b.service_name}</p>
                        {(b.addons || []).length > 0 && (
                          <p className="truncate text-[11px] text-primary">
                            + {b.addons.map((a) => a.name).join(", ")}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[180px]">
                        <p className="truncate text-sm">{b.customer?.name}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {b.customer?.phone} · {b.customer?.email}
                        </p>
                      </TableCell>
                      <TableCell className="whitespace-nowrap font-mono-nums font-medium">
                        {formatEuro(b.amount)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`whitespace-nowrap text-[11px] ${STATUS_BADGES[b.status] || ""}`}>
                          {STATUS_LABELS[b.status] || b.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1.5">
                          {b.status === "en_attente_validation" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 border-green-600/40 text-green-400 hover:bg-green-600/10"
                              onClick={() => updateBookingStatus(b.id, "reservee")}
                              data-testid={`admin-validate-${b.id}`}
                              title="Valider (email de confirmation envoyé)"
                            >
                              <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                              Valider
                            </Button>
                          )}
                          {b.status === "reservee" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 border-green-600/40 text-green-400 hover:bg-green-600/10"
                              onClick={() => updateBookingStatus(b.id, "confirmee")}
                              data-testid={`admin-confirm-${b.id}`}
                              title="Marquer comme payée"
                            >
                              <Euro className="mr-1 h-3.5 w-3.5" />
                              Payée
                            </Button>
                          )}
                          {b.status !== "annulee" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 border-destructive/40 text-destructive hover:bg-destructive/10"
                              onClick={() => updateBookingStatus(b.id, "annulee")}
                              data-testid={`admin-cancel-${b.id}`}
                              title="Annuler (email envoyé au client)"
                            >
                              <XCircle className="mr-1 h-3.5 w-3.5" />
                              Annuler
                            </Button>
                          )}
                          {b.status === "annulee" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 text-muted-foreground"
                              onClick={() => updateBookingStatus(b.id, "reservee")}
                              data-testid={`admin-restore-${b.id}`}
                            >
                              <Hourglass className="mr-1 h-3.5 w-3.5" />
                              Réactiver
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-muted-foreground hover:text-destructive"
                            onClick={() => blockClientFromBooking(b)}
                            data-testid={`admin-block-client-${b.id}`}
                            title="Bloquer ce client"
                          >
                            <UserX className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ---------- Devis mariage ---------- */}
        {!IS_STUDIO && (
          <TabsContent value="mariages" className="mt-6">
            <p className="mb-4 text-sm text-muted-foreground">
              Toutes les demandes de devis mariage reçues depuis le site. Mettez à jour le statut au
              fil de votre suivi — le client reçoit une confirmation automatique, puis une{" "}
              <span className="font-medium text-foreground">relance automatique après 3 jours</span>{" "}
              si la demande reste en « Nouveau » ou « Contacté ».
            </p>
            {weddingQuotes.length === 0 ? (
              <Card className="border-border bg-card">
                <CardContent className="p-8 text-center text-sm text-muted-foreground">
                  Aucune demande de devis mariage pour le moment.
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {weddingQuotes.map((q) => (
                  <Card key={q.id} className="border-border bg-card" data-testid={`wedding-quote-${q.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-heading text-base font-semibold">
                            {q.first_name} {q.last_name}
                          </h3>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            Reçue le {q.created_at ? format(parseISO(q.created_at), "d MMM yyyy 'à' HH:mm", { locale: fr }) : "—"}
                          </p>
                          {q.relance_sent_at && (
                            <p className="mt-0.5 text-xs font-medium text-primary" data-testid={`wedding-quote-relance-${q.id}`}>
                              Relance envoyée le {format(parseISO(q.relance_sent_at), "d MMM yyyy", { locale: fr })}
                            </p>
                          )}
                        </div>
                        <Select
                          value={q.status || "nouveau"}
                          onValueChange={(v) => updateWeddingQuoteStatus(q.id, v)}
                        >
                          <SelectTrigger className="w-[150px]" data-testid={`wedding-quote-status-${q.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(WEDDING_STATUS_LABELS).map(([value, label]) => (
                              <SelectItem key={value} value={value}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="mt-4 space-y-1.5 text-sm">
                        <p>
                          <span className="text-muted-foreground">Téléphone : </span>
                          <a href={`tel:${q.phone}`} className="font-medium text-primary hover:underline">
                            {q.phone}
                          </a>
                        </p>
                        <p>
                          <span className="text-muted-foreground">Email : </span>
                          <a href={`mailto:${q.email}`} className="font-medium text-primary hover:underline">
                            {q.email}
                          </a>
                        </p>
                        {q.wedding_date && (
                          <p>
                            <span className="text-muted-foreground">Date du mariage : </span>
                            <span className="font-medium">{q.wedding_date}</span>
                          </p>
                        )}
                        {q.formule && (
                          <p>
                            <span className="text-muted-foreground">Formule : </span>
                            <span className="font-medium">{q.formule}</span>
                          </p>
                        )}
                        {q.message && (
                          <p className="rounded-md bg-secondary/50 p-2.5 text-xs leading-relaxed text-foreground/85">
                            {q.message}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* ---------- Tarifs ---------- */}
        <TabsContent value="tarifs" className="mt-6">
          <p className="mb-4 text-sm text-muted-foreground">
            Modifiez le tarif <span className="font-medium text-foreground">sur place</span> : le tarif en
            ligne est calculé automatiquement avec la remise de{" "}
            <span className="text-primary">-{settings?.online_discount_percent ?? 0}%</span> (modifiable
            dans Paramètres).
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {services.map((svc) => {
              const edits = priceEdits[svc.id] || {};
              return (
                <Card key={svc.id} className="border-border bg-card" data-testid={`admin-service-${svc.slug}`}>
                  <CardContent className="p-5">
                    <h3 className="font-heading text-base font-semibold">{svc.name}</h3>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {svc.category === "mariage" || svc.category === "mariage_option"
                        ? `Mariage · ${svc.category === "mariage" ? "Formule" : "Option"} · sur devis`
                        : `${STUDIO_LABELS[svc.studio_type]} · ${
                            svc.pricing_type === "hourly"
                              ? "Tarif horaire"
                              : svc.duration_hours
                              ? `Forfait ${svc.duration_hours}h`
                              : "Forfait"
                          }`}
                      {svc.from_price ? " · à partir de" : ""}
                    </p>
                    <details className="mt-3 rounded-md border border-border bg-background/50 px-3 py-2.5" data-testid={`admin-texts-block-${svc.slug}`}>
                      <summary className="cursor-pointer text-xs font-medium">
                        Modifier les textes (nom, description, caractéristiques)
                      </summary>
                      <div className="mt-3 space-y-3">
                        <div className="space-y-1">
                          <Label className="text-[11px] text-muted-foreground">Nom de la prestation</Label>
                          <Input
                            className="h-8"
                            value={edits.name ?? svc.name}
                            onChange={(e) =>
                              setPriceEdits((prev) => ({
                                ...prev,
                                [svc.id]: { ...prev[svc.id], name: e.target.value },
                              }))
                            }
                            data-testid={`admin-name-${svc.slug}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[11px] text-muted-foreground">Description</Label>
                          <Textarea
                            rows={3}
                            value={edits.description ?? (svc.description || "")}
                            onChange={(e) =>
                              setPriceEdits((prev) => ({
                                ...prev,
                                [svc.id]: { ...prev[svc.id], description: e.target.value },
                              }))
                            }
                            data-testid={`admin-description-${svc.slug}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[11px] text-muted-foreground">
                            Caractéristiques (une par ligne — puces affichées sur les formules mariage)
                          </Label>
                          <Textarea
                            rows={3}
                            value={edits.features ?? (svc.features || []).join("\n")}
                            onChange={(e) =>
                              setPriceEdits((prev) => ({
                                ...prev,
                                [svc.id]: { ...prev[svc.id], features: e.target.value },
                              }))
                            }
                            data-testid={`admin-features-${svc.slug}`}
                          />
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          Cliquez sur Enregistrer en bas de la carte pour appliquer.
                        </p>
                      </div>
                    </details>
                    <div className="mt-4 grid grid-cols-2 items-end gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">
                          Sur place {svc.pricing_type === "hourly" ? "(€/h)" : "(€)"}
                          {svc.price_tiers ? " · = palier 1h (synchronisé)" : ""}
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          placeholder={String(svc.price_onsite)}
                          value={edits.price_onsite ?? ""}
                          onChange={(e) =>
                            setPriceEdits((prev) => ({
                              ...prev,
                              [svc.id]: { ...prev[svc.id], price_onsite: e.target.value },
                            }))
                          }
                          data-testid={`admin-price-onsite-${svc.slug}`}
                        />
                      </div>
                      <div className="rounded-md bg-[hsl(var(--studio-yellow)/0.10)] px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-primary">En ligne (auto)</p>
                        <p className="font-mono-nums font-semibold text-primary">
                          {formatEuro(svc.price_online)}
                          {svc.pricing_type === "hourly" ? "/h" : ""}
                        </p>
                      </div>
                    </div>
                    {svc.price_tiers && (
                      <div className="mt-4">
                        <Label className="text-xs text-muted-foreground">
                          Paliers dégressifs (€ pour la durée totale)
                        </Label>
                        <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
                          {Object.entries(svc.price_tiers)
                            .sort((a, b) => Number(a[0]) - Number(b[0]))
                            .map(([h, p]) => (
                              <div key={h} className="space-y-1">
                                <p className="text-center text-[11px] font-medium text-muted-foreground">{h}h</p>
                                <Input
                                  type="number"
                                  min="0"
                                  step="1"
                                  className="h-8 text-center text-xs"
                                  placeholder={String(p)}
                                  value={edits.tiers?.[h] ?? ""}
                                  onChange={(e) =>
                                    setPriceEdits((prev) => ({
                                      ...prev,
                                      [svc.id]: {
                                        ...prev[svc.id],
                                        tiers: { ...(prev[svc.id]?.tiers || {}), [h]: e.target.value },
                                      },
                                    }))
                                  }
                                  data-testid={`admin-tier-${svc.slug}-${h}`}
                                />
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                    <div className="mt-4 flex items-center justify-between rounded-md border border-border bg-background/50 px-3 py-2.5">
                      <div>
                        <p className="text-xs font-medium">Afficher « à partir de »</p>
                        <p className="text-[11px] text-muted-foreground">
                          {svc.from_price
                            ? `Affiché : à partir de ${formatEuro(svc.price_onsite)}${svc.pricing_type === "hourly" ? "/h" : ""}`
                            : "Affiché : prix direct"}
                        </p>
                      </div>
                      <Switch
                        checked={!!svc.from_price}
                        onCheckedChange={(checked) => toggleFromPrice(svc, checked)}
                        data-testid={`admin-from-price-toggle-${svc.slug}`}
                      />
                    </div>
                    <div className="mt-3 rounded-md border border-border bg-background/50 px-3 py-2.5" data-testid={`admin-promo-block-${svc.slug}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium">
                          Promotion
                          {svc.promo_live && (
                            <span className="ml-1.5 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                              -{Math.round(svc.promo_percent)}% en cours
                            </span>
                          )}
                        </p>
                        <Switch
                          checked={!!svc.promo_active}
                          onCheckedChange={(checked) => togglePromo(svc, checked)}
                          data-testid={`admin-promo-toggle-${svc.slug}`}
                        />
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-[11px] text-muted-foreground">Remise (%)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="90"
                            step="1"
                            className="h-8"
                            placeholder={svc.promo_percent ? String(svc.promo_percent) : "ex : 20"}
                            value={edits.promo_percent ?? ""}
                            onChange={(e) =>
                              setPriceEdits((prev) => ({
                                ...prev,
                                [svc.id]: { ...prev[svc.id], promo_percent: e.target.value },
                              }))
                            }
                            data-testid={`admin-promo-percent-${svc.slug}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[11px] text-muted-foreground">Jusqu'au (optionnel)</Label>
                          <Input
                            type="date"
                            className="h-8"
                            value={edits.promo_until ?? (svc.promo_until || "")}
                            onChange={(e) =>
                              setPriceEdits((prev) => ({
                                ...prev,
                                [svc.id]: { ...prev[svc.id], promo_until: e.target.value },
                              }))
                            }
                            data-testid={`admin-promo-until-${svc.slug}`}
                          />
                        </div>
                      </div>
                      <p className="mt-1.5 text-[11px] text-muted-foreground">
                        Prix barré affiché sur le site et remise appliquée au paiement. Enregistrez après modification.
                      </p>
                    </div>
                    {(() => {
                      const base = svc.price_tiers ? Number(svc.price_tiers["1"]) : Number(svc.price_onsite);
                      const live = !!svc.promo_live;
                      const shown = live ? Math.round(base * (1 - Number(svc.promo_percent) / 100) * 100) / 100 : base;
                      const online = Math.round(shown * (1 - Number(settings?.online_discount_percent ?? 0) / 100) * 100) / 100;
                      return (
                        <div className="mt-3 rounded-md bg-[hsl(var(--studio-yellow)/0.08)] px-3 py-2" data-testid={`admin-preview-${svc.slug}`}>
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Aperçu public</p>
                          <p className="mt-0.5 font-mono-nums text-sm font-semibold text-primary">
                            {svc.from_price && (
                              <span className="mr-1 text-[11px] font-normal text-muted-foreground">à partir de</span>
                            )}
                            {live && (
                              <s className="mr-1 text-xs font-normal text-muted-foreground">{formatEuro(base)}</s>
                            )}
                            {formatEuro(shown)}
                            {svc.pricing_type === "hourly" ? "/h" : ""}
                            {live && (
                              <span className="ml-1.5 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                                -{Math.round(svc.promo_percent)}%
                                {svc.promo_until ? ` jusqu'au ${svc.promo_until.split("-").reverse().join("/")}` : ""}
                              </span>
                            )}
                            <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                              · en ligne : {formatEuro(online)}
                            </span>
                          </p>
                        </div>
                      );
                    })()}
                    <Button size="sm" className="mt-3" onClick={() => savePrices(svc)} data-testid={`admin-save-price-${svc.slug}`}>
                      Enregistrer
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* ---------- Médias ---------- */}
        {!IS_STUDIO && (
          <TabsContent value="medias" className="mt-6">
            <Card className="border-border bg-card">
              <CardContent className="p-5 sm:p-6">
                <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
                  <ImagePlus className="h-4 w-4 text-primary" />
                  Ajouter un média (photo, teaser, clip…)
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Chaque média est classé par thème : les visiteurs filtrent la galerie du site
                  section par section (Teasers, Mariages, Clips, Photos…). Les médias « Danseurs &
                  figurants » s'affichent dans leur section dédiée. Pour chaque thème vous pouvez{" "}
                  <span className="font-medium text-foreground">importer un fichier</span> ou{" "}
                  <span className="font-medium text-foreground">
                    coller un lien (YouTube, Vimeo, Instagram, TikTok…)
                  </span>
                  .
                </p>
                <form onSubmit={submitMedia} className="mt-4 space-y-4">
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Thème / section</Label>
                      <Select
                        value={mediaForm.category}
                        onValueChange={(v) => setMediaForm({ ...mediaForm, category: v })}
                      >
                        <SelectTrigger className="w-[210px]" data-testid="media-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {["teaser", "mariage", "clip", "photo", "book", "podcast", "corporate", "casting", "autre"].map(
                            (cat) => (
                              <SelectItem key={cat} value={cat}>
                                {MEDIA_CATEGORY_LABELS[cat]}
                              </SelectItem>
                            )
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Titre (affiché sur le site)</Label>
                      <Input
                        className="w-[220px]"
                        placeholder="Ex : Clip Kenza — teaser"
                        value={mediaForm.title}
                        onChange={(e) => setMediaForm({ ...mediaForm, title: e.target.value })}
                        data-testid="media-title-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Source</Label>
                      <Select
                        value={mediaForm.mode}
                        onValueChange={(v) => setMediaForm({ ...mediaForm, mode: v })}
                      >
                        <SelectTrigger className="w-[210px]" data-testid="media-mode-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="file">Fichier (photo / vidéo)</SelectItem>
                          <SelectItem value="link">Lien (YouTube, Vimeo, Instagram, TikTok…)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {mediaForm.mode === "file" ? (
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">
                        Fichier — photos : jpg / png / webp · vidéos : mp4 / mov / webm
                      </Label>
                      <Input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,.gif,.mp4,.mov,.webm,.m4v"
                        onChange={(e) => setMediaFile(e.target.files?.[0] || null)}
                        data-testid="media-file-input"
                      />
                      {mediaUploading && (
                        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-secondary">
                          <div
                            className="h-full bg-primary transition-all duration-200"
                            style={{ width: `${mediaProgress}%` }}
                            data-testid="media-upload-progress"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">
                        Lien YouTube, Vimeo, Instagram, TikTok…
                      </Label>
                      <Input
                        placeholder="https://www.youtube.com/watch?v=… ou https://www.instagram.com/reel/…"
                        value={mediaForm.link}
                        onChange={(e) => setMediaForm({ ...mediaForm, link: e.target.value })}
                        data-testid="media-link-input"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        YouTube et Vimeo s'affichent intégrés sur le site — Instagram, TikTok et
                        autres liens ouvrent la publication directement.
                      </p>
                    </div>
                  )}
                  <Button type="submit" disabled={mediaUploading} data-testid="media-submit-button">
                    {mediaUploading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : mediaForm.mode === "link" ? (
                      <Link2 className="mr-2 h-4 w-4" />
                    ) : (
                      <ImagePlus className="mr-2 h-4 w-4" />
                    )}
                    {mediaUploading
                      ? `Envoi en cours… ${mediaForm.mode === "file" ? `${mediaProgress}%` : ""}`
                      : "Ajouter le média"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {mediaItems.length === 0 && (
                <p className="col-span-full text-sm text-muted-foreground">
                  Aucun média pour le moment — la galerie du site affiche les images d'exemple.
                </p>
              )}
              {mediaItems.map((item) => (
                <Card key={item.id} className="overflow-hidden border-border bg-card" data-testid={`media-card-${item.id}`}>
                  <div className="relative h-40 bg-black">
                    {item.media_type === "photo" ? (
                      <img src={mediaSrc(item.url)} alt={item.title || "média"} className="h-full w-full object-cover" />
                    ) : item.source === "link" &&
                      (item.url.includes("youtube.com/embed") || item.url.includes("player.vimeo.com")) ? (
                      <iframe
                        src={mediaSrc(item.url)}
                        title={item.title || "vidéo"}
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : item.source === "link" ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-black via-neutral-900 to-black text-white"
                      >
                        <Link2 className="h-8 w-8 text-primary" />
                        <span className="px-3 text-center text-xs font-medium">
                          Voir sur{" "}
                          {item.url.includes("instagram.com")
                            ? "Instagram"
                            : item.url.includes("tiktok.com")
                            ? "TikTok"
                            : item.url.includes("snapchat.com")
                            ? "Snapchat"
                            : "le lien"}{" "}
                          ↗
                        </span>
                      </a>
                    ) : (
                      <video src={mediaSrc(item.url)} controls className="h-full w-full object-contain" />
                    )}
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold">{item.title || "Sans titre"}</p>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          {MEDIA_CATEGORY_LABELS[item.category] || item.category} ·{" "}
                          {item.media_type === "photo" ? "Photo" : item.source === "link" ? "Vidéo (lien)" : "Vidéo (fichier)"}
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className={item.active ? "border-green-600/40 text-green-500" : "border-border text-muted-foreground"}
                      >
                        {item.active ? "Visible" : "Masqué"}
                      </Badge>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleMediaActive(item)}
                        data-testid={`media-toggle-${item.id}`}
                      >
                        {item.active ? "Masquer" : "Rendre visible"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive hover:bg-destructive/10"
                        onClick={() => deleteMedia(item)}
                        data-testid={`media-delete-${item.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        )}

        {/* ---------- Indisponibilites ---------- */}
        <TabsContent value="indisponibilites" className="mt-6">
          <Card className="border-border bg-card">
            <CardContent className="p-5 sm:p-6">
              <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
                <Wrench className="h-4 w-4 text-primary" />
                Bloquer des créneaux (maintenance, absence…)
              </h3>
              <form onSubmit={createBlockedSlot} className="mt-4 flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Date</Label>
                  <Input
                    type="date"
                    value={slotForm.date}
                    onChange={(e) => setSlotForm({ ...slotForm, date: e.target.value })}
                    className="w-[160px]"
                    data-testid="blocked-slot-date"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">De</Label>
                  <Select value={slotForm.start_hour} onValueChange={(v) => setSlotForm({ ...slotForm, start_hour: v })}>
                    <SelectTrigger className="w-[100px]" data-testid="blocked-slot-start">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map((h) => (
                        <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">À</Label>
                  <Select value={slotForm.end_hour} onValueChange={(v) => setSlotForm({ ...slotForm, end_hour: v })}>
                    <SelectTrigger className="w-[100px]" data-testid="blocked-slot-end">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map((h) => (
                        <SelectItem key={h + 1} value={String(h + 1)}>{h + 1 === 24 ? "24:00" : formatHour(h + 1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 space-y-1.5" style={{ minWidth: "160px" }}>
                  <Label className="text-xs text-muted-foreground">Raison</Label>
                  <Input
                    placeholder="Maintenance console…"
                    value={slotForm.reason}
                    onChange={(e) => setSlotForm({ ...slotForm, reason: e.target.value })}
                    data-testid="blocked-slot-reason"
                  />
                </div>
                <Button type="submit" data-testid="blocked-slot-submit">Bloquer</Button>
              </form>
            </CardContent>
          </Card>

          <div className="mt-5 overflow-x-auto rounded-xl border border-border">
            <Table data-testid="blocked-slots-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Date</TableHead>
                  <TableHead>Horaire</TableHead>
                  <TableHead>Espace</TableHead>
                  <TableHead>Raison</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blockedSlots.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                      Aucune indisponibilité programmée
                    </TableCell>
                  </TableRow>
                )}
                {blockedSlots.map((s) => (
                  <TableRow key={s.id} className="border-border">
                    <TableCell className="capitalize">
                      {format(parseISO(s.date), "EEE d MMM yyyy", { locale: fr })}
                    </TableCell>
                    <TableCell className="font-mono-nums">
                      {formatHour(s.start_hour)}–{s.end_hour === 24 ? "24:00" : formatHour(s.end_hour)}
                    </TableCell>
                    <TableCell className="text-xs">{STUDIO_LABELS[s.studio_type]}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">{s.reason}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-muted-foreground hover:text-destructive"
                        onClick={() => deleteBlockedSlot(s.id)}
                        data-testid={`delete-blocked-slot-${s.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ---------- Clients bloques ---------- */}
        <TabsContent value="clients" className="mt-6">
          <Card className="border-border bg-card">
            <CardContent className="p-5 sm:p-6">
              <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
                <UserX className="h-4 w-4 text-primary" />
                Bloquer un client (par email)
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Un client bloqué ne peut plus effectuer de réservation (en ligne ou sur place).
              </p>
              <form onSubmit={blockClientManual} className="mt-4 flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Email du client</Label>
                  <Input
                    type="email"
                    placeholder="client@exemple.fr"
                    value={clientForm.email}
                    onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                    className="w-[240px]"
                    data-testid="block-client-email"
                  />
                </div>
                <div className="flex-1 space-y-1.5" style={{ minWidth: "160px" }}>
                  <Label className="text-xs text-muted-foreground">Raison (optionnel)</Label>
                  <Input
                    placeholder="No-show répété…"
                    value={clientForm.reason}
                    onChange={(e) => setClientForm({ ...clientForm, reason: e.target.value })}
                    data-testid="block-client-reason"
                  />
                </div>
                <Button type="submit" disabled={!clientForm.email} data-testid="block-client-submit">
                  Bloquer
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="mt-5 overflow-x-auto rounded-xl border border-border">
            <Table data-testid="blocked-clients-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Email</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead>Raison</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blockedClients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                      Aucun client bloqué
                    </TableCell>
                  </TableRow>
                )}
                {blockedClients.map((c) => (
                  <TableRow key={c.id} className="border-border">
                    <TableCell className="text-sm">{c.email}</TableCell>
                    <TableCell className="text-sm">{c.name || "—"}</TableCell>
                    <TableCell className="font-mono-nums text-sm">{c.phone || "—"}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                      {c.reason || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8"
                        onClick={() => unblockClient(c.id)}
                        data-testid={`unblock-client-${c.id}`}
                      >
                        Débloquer
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ---------- Parametres ---------- */}
        <TabsContent value="textes" className="mt-6">
          <Card className="border-border bg-card">
            <CardContent className="p-5">
              <h3 className="font-heading text-base font-semibold">Textes du site</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Modifiez les titres et textes des pages publiques. Videz un champ et enregistrez pour
                revenir au texte d'origine.
              </p>
              <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-2">
                {SITE_TEXT_SECTIONS.map((section) => (
                  <div key={section.title} className="rounded-md border border-border bg-background/50 p-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-primary">
                      {section.title}
                    </h4>
                    <div className="mt-3 space-y-3">
                      {section.fields.map((f) => (
                        <div key={f.key} className="space-y-1">
                          <Label className="text-[11px] text-muted-foreground">{f.label}</Label>
                          {f.multiline ? (
                            <Textarea
                              rows={2}
                              value={textEdits[f.key] ?? siteTexts[f.key] ?? ""}
                              onChange={(e) =>
                                setTextEdits((prev) => ({ ...prev, [f.key]: e.target.value }))
                              }
                              data-testid={`admin-text-${f.key}`}
                            />
                          ) : (
                            <Input
                              className="h-8"
                              value={textEdits[f.key] ?? siteTexts[f.key] ?? ""}
                              onChange={(e) =>
                                setTextEdits((prev) => ({ ...prev, [f.key]: e.target.value }))
                              }
                              data-testid={`admin-text-${f.key}`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <Button className="mt-6" onClick={saveSiteTexts} data-testid="admin-save-texts">
                Enregistrer les textes
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="parametres" className="mt-6">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Card className="border-border bg-card">
              <CardContent className="p-5 sm:p-6">
                <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
                  <Settings2 className="h-4 w-4 text-primary" />
                  Règles de réservation
                </h3>
                <div className="mt-5 space-y-5">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <Label className="text-sm">Validation manuelle des réservations sur place</Label>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Si activé, vous devez valider chaque réservation « sur place » avant l'envoi de
                        l'email de confirmation au client.
                      </p>
                    </div>
                    <Switch
                      checked={settingsEdits.manual_confirmation ?? settings?.manual_confirmation ?? true}
                      onCheckedChange={(v) => setSettingsEdits({ ...settingsEdits, manual_confirmation: v })}
                      data-testid="settings-manual-confirmation"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Remise en ligne (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="90"
                        placeholder={String(settings?.online_discount_percent ?? 0)}
                        value={settingsEdits.online_discount_percent ?? ""}
                        onChange={(e) => setSettingsEdits({ ...settingsEdits, online_discount_percent: e.target.value })}
                        data-testid="settings-discount"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Email société — {SITE.fullName}</Label>
                    <Input
                      type="email"
                      placeholder={settings?.[`company_email_${SITE_STUDIO_TYPE}`] || ""}
                      value={settingsEdits[`company_email_${SITE_STUDIO_TYPE}`] ?? ""}
                      onChange={(e) =>
                        setSettingsEdits({ ...settingsEdits, [`company_email_${SITE_STUDIO_TYPE}`]: e.target.value })
                      }
                      data-testid="settings-email-site"
                    />
                  </div>
                  <Button onClick={saveSettings} data-testid="settings-save">
                    Enregistrer les paramètres
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-5">
              <Card className="border-border bg-card">
                <CardContent className="p-5 sm:p-6">
                  <h3 className="font-heading text-base font-semibold">Liens réseaux sociaux</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Modifiez les liens des bulles réseaux affichées sur tout le site (utile si un
                    compte saute ou change de nom).
                  </p>
                  <div className="mt-4 space-y-3">
                    {[
                      { key: "social_instagram", label: "Instagram", placeholder: "https://www.instagram.com/…" },
                      { key: "social_tiktok", label: "TikTok", placeholder: "https://www.tiktok.com/@…" },
                      { key: "social_snapchat", label: "Snapchat", placeholder: "https://www.snapchat.com/add/…" },
                      { key: "contact_phone", label: "Numéro de téléphone affiché sur le site", placeholder: "07 43 78 03 93" },
                    ].map(({ key, label, placeholder }) => (
                      <div key={key} className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">{label}</Label>
                        <Input
                          placeholder={settings?.[key] || placeholder}
                          value={settingsEdits[key] ?? ""}
                          onChange={(e) => setSettingsEdits({ ...settingsEdits, [key]: e.target.value })}
                          data-testid={`settings-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                  <Button className="mt-4" onClick={saveSettings} data-testid="settings-social-save">
                    Enregistrer les liens
                  </Button>
                </CardContent>
              </Card>

              <Card className={settings?.smtp_configured ? "border-green-600/40 bg-card" : "border-orange-500/40 bg-card"}>
                <CardContent className="p-5 sm:p-6">
                  <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
                    {settings?.smtp_configured ? (
                      <ShieldCheck className="h-4 w-4 text-green-400" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-orange-400" />
                    )}
                    Envoi d'emails
                  </h3>
                  {settings?.smtp_configured ? (
                    <p className="mt-2 text-sm text-muted-foreground" data-testid="smtp-status">
                      Configuré — les emails sont envoyés depuis{" "}
                      <span className="font-medium text-foreground">{settings?.sender_email}</span>.
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-muted-foreground" data-testid="smtp-status">
                      <span className="font-medium text-orange-400">Non configuré.</span> Les emails sont
                      enregistrés mais pas envoyés. Fournissez le mot de passe d'application Gmail de{" "}
                      <span className="font-medium text-foreground">{settings?.sender_email}</span> à votre
                      développeur pour activer l'envoi réel.
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card className="border-border bg-card">
                <CardContent className="p-5 sm:p-6">
                  <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
                    <Mail className="h-4 w-4 text-primary" />
                    Derniers emails
                  </h3>
                  <div className="mt-4 max-h-[320px] space-y-2 overflow-y-auto" data-testid="email-logs-list">
                    {emailLogs.length === 0 && (
                      <p className="text-sm text-muted-foreground">Aucun email pour le moment</p>
                    )}
                    {emailLogs.slice(0, 20).map((log) => (
                      <div key={log.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-xs font-medium">{log.subject}</p>
                          <p className="truncate text-[11px] text-muted-foreground">→ {log.to}</p>
                        </div>
                        <Badge variant="outline" className={`flex-shrink-0 text-[10px] ${EMAIL_STATUS_BADGES[log.status] || ""}`}>
                          {EMAIL_STATUS_LABELS[log.status] || log.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
